/**
 * Lab Market Types → CM ticker `marketName` / market picker.
 * No separate CM-API `marketType` — category narrows the markets list and,
 * when no specific market is chosen, Match Odds is sent as exact `marketName`.
 */

export const TICKER_MARKET_TYPES = [
  'Match Odds',
  'Bookmaker',
  'Fancy',
  'Linesession',
] as const

export type TickerMarketType = (typeof TICKER_MARKET_TYPES)[number]

const TYPE_SET = new Set<string>(TICKER_MARKET_TYPES)

export function sanitizeMarketType(raw: string): string {
  const value = raw.trim()
  if (!value || value.toLowerCase() === 'all') return 'all'
  return TYPE_SET.has(value) ? value : 'all'
}

/** Narrow filter-options markets to the Lab market-type bucket. */
export function marketsMatchingType(
  markets: readonly string[],
  type: string,
): string[] {
  if (!type || type === 'all') return [...markets]
  const t = type.toLowerCase()
  return markets.filter((name) => marketMatchesType(name, t))
}

function marketMatchesType(name: string, typeLower: string): boolean {
  const n = name.toLowerCase()
  if (typeLower === 'match odds') {
    return /match\s*odds/.test(n)
  }
  if (typeLower === 'bookmaker') {
    return /book\s*maker|\bbm\b/.test(n)
  }
  if (typeLower === 'linesession') {
    return /line\s*session|linesession|line\s*ladder|lineladder/.test(n)
  }
  if (typeLower === 'fancy') {
    if (/line\s*session|linesession|line\s*ladder|lineladder|match\s*odds|book\s*maker|\bbm\b/.test(n)) {
      return false
    }
    return /fancy|session/.test(n)
  }
  return false
}

/**
 * Resolve `marketName` for `POST /v1/book/ticker`.
 * Specific market wins; else Match Odds category alone is an exact name.
 */
export function marketNameForRequest(
  market: string,
  marketType: string,
): string | undefined {
  const specific = market.trim()
  if (specific) return specific
  if (marketType === 'Match Odds') return 'Match Odds'
  return undefined
}
